// Phase 2.3 Lite — Reusable link row used by Template Library and Event Setup.
// One visual treatment for: Internal preview, Public demo, Client review,
// Guest invitation. The container decides which actions to wire (Copy / Open /
// Generate / Disable / Regenerate); this component only renders them.

import React from 'react';
import {
  Copy, ExternalLink, Link as LinkIcon, Loader2, Plus, EyeOff, RotateCcw,
} from 'lucide-react';
import LinkStatusBadge from './LinkStatusBadge';
import {
  copyLinkWithToast, getLinkLabel, getLinkDescription, toAbsoluteUrl,
} from '../../lib/utils/linkUtils';

export default function LinkActionRow({
  type,
  url,                  // path or absolute URL; empty/null = "not generated"
  status,               // 'live' | 'disabled' | 'internal' | 'private' | 'draft' | ...
  disabled = false,     // when true, generate button is locked (e.g. unapproved template)
  disabledReason,       // tooltip text shown on disabled generate
  helperText,           // optional secondary description below the URL
  busy = false,         // shows a small spinner on the action area
  onCopy,
  onOpen,
  onGenerate,
  onDisable,
  onRegenerate,
  testidPrefix,         // optional unique prefix for testids
}) {
  const label = getLinkLabel(type);
  const description = getLinkDescription(type);
  const absoluteUrl = url ? toAbsoluteUrl(url) : '';
  const tid = (suffix) => `${testidPrefix || `link-row-${type}`}-${suffix}`;

  const handleCopy = async () => {
    if (!url) return;
    const copied = await copyLinkWithToast(url, `${label} link`);
    if (copied) onCopy?.(copied);
  };
  const handleOpen = () => {
    if (!url) return;
    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    onOpen?.(absoluteUrl);
  };

  return (
    <div
      className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
      data-testid={tid('container')}
    >
      <LinkIcon className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <strong className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</strong>
          {status && <LinkStatusBadge status={status} testid={tid('status')} />}
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        {url ? (
          <code
            className="block mt-1.5 text-[11px] text-gray-700 dark:text-gray-300 truncate"
            data-testid={tid('url')}
          >
            {absoluteUrl}
          </code>
        ) : (
          helperText && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 italic" data-testid={tid('helper')}>
              {helperText}
            </p>
          )
        )}
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0">
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <>
            {url && (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                  data-testid={tid('copy-btn')}
                >
                  <Copy className="w-3 h-3" />
                  Copy
                </button>
                <button
                  onClick={handleOpen}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                  data-testid={tid('open-btn')}
                >
                  <ExternalLink className="w-3 h-3" />
                  Open
                </button>
              </div>
            )}
            {!url && onGenerate && (
              <button
                onClick={onGenerate}
                disabled={disabled}
                title={disabled ? (disabledReason || '') : ''}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={tid('generate-btn')}
              >
                <Plus className="w-3 h-3" />
                Generate
              </button>
            )}
            {url && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid={tid('regenerate-btn')}
              >
                <RotateCcw className="w-3 h-3" />
                Regenerate
              </button>
            )}
            {url && onDisable && (
              <button
                onClick={onDisable}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid={tid('disable-btn')}
              >
                <EyeOff className="w-3 h-3" />
                Disable
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
